import { countFenceMarkers } from '@aline/common/serializers/markdown/code-fence';
import type { Element } from 'hast';
import type { Root } from 'mdast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';
import { z } from 'zod';

import { dataIdMap } from './remark-constants';
import { setHTMLProperties } from './remark-helpers';

/**
 * This plugin adds `messageId`, `threadId`, and `isNodeStreaming` to all `code` nodes.
 * A code block is streaming iff it's an unclosed block and the message is streaming.
 */
export const remarkCodeBlock: Plugin<
  [
    {
      isMessageStreaming: boolean;
      messageId: UUID;
      threadId: UUID;
    },
  ],
  Root
> = ({ isMessageStreaming, messageId, threadId }) => {
  return (tree, file) => {
    const fullMarkdown = String(file);

    visit(tree, 'code', node => {
      const end = node.position?.end.offset ?? 0;
      const start = node.position?.start.offset ?? 0;
      const slice = fullMarkdown.slice(start, end);
      /**
       * Fence recognition is shared with the server's finalize checks via
       * `code-fence.ts`, so a change to what counts as a fence cannot leave the
       * two disagreeing about whether a turn showed the user anything.
       */
      const isCodeBlockClosed = countFenceMarkers(slice) >= 2;
      const isNodeStreaming = Boolean(isMessageStreaming && !isCodeBlockClosed);

      setHTMLProperties(node, {
        [dataIdMap.isNodeStreaming]: isNodeStreaming,
        [dataIdMap.messageId]: messageId,
        [dataIdMap.threadId]: threadId,
        [dataIdMap.code]: node.value,
      });
    });
  };
};

const nodePropertiesSchema = z.object({
  code: z.string(),
  isNodeStreaming: z.boolean(),
  messageId: z.string(),
  threadId: z.string(),
});
export type CodeBlockInfo = z.infer<typeof nodePropertiesSchema>;

export function getCodeBlockInfoFromNode(node: Element | undefined): CodeBlockInfo | null {
  const result = nodePropertiesSchema.safeParse({
    code: node?.properties[dataIdMap.code],
    isNodeStreaming: Boolean(node?.properties[dataIdMap.isNodeStreaming]),
    messageId: node?.properties[dataIdMap.messageId],
    threadId: node?.properties[dataIdMap.threadId],
  });
  if (!result.success) return null;

  return {
    code: result.data.code,
    isNodeStreaming: Boolean(result.data.isNodeStreaming),
    messageId: result.data.messageId,
    threadId: result.data.threadId,
  };
}
